import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import 'videojs-contrib-quality-levels'
import VideoQuality, { initialVideoBandwidth } from './video-quality'

import styles from './video-gallery.module.scss'

export interface PropsVideoGallery {
  src?: string
  thumbnail?: string
  videoManifest?: string
  visible?: boolean
}

const VideoGallery: FC<PropsVideoGallery> = (props) => {
  const { src, thumbnail, videoManifest, visible } = props
  const [ready, setReady] = useState(false)
  const [videoRatio, setVideoRatio] = useState<number | null>(null)
  const [posterRatio, setPosterRatio] = useState<number | null>(null)
  const [size, setSize] = useState<{ width: number; height: number }>()
  const boxRef = useRef<HTMLDivElement | null>(null)
  const videoNode = useRef<HTMLVideoElement | null>(null)
  const player = useRef<videojs.Player | null>(null)
  const ratio = videoRatio || posterRatio || 16 / 9

  useEffect(() => {
    setPosterRatio(null)
    if (!thumbnail) return
    const image = new Image()
    image.onload = () => {
      if (image.naturalWidth && image.naturalHeight) setPosterRatio(image.naturalWidth / image.naturalHeight)
    }
    image.src = thumbnail
    return () => {
      image.onload = null
    }
  }, [thumbnail])

  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box || !visible) return
    const update = () => {
      if (!box.clientWidth || !box.clientHeight) return
      const width = Math.min(box.clientWidth, box.clientHeight * ratio)
      setSize({ width, height: width / ratio })
    }
    update()
    window.addEventListener('resize', update)
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update)
    observer?.observe(box)
    return () => {
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [ratio, visible, ready])

  const videoJsOptions: videojs.PlayerOptions = useMemo(() => {
    return {
      autoplay: false,
      controls: true,
      controlBar: { pictureInPictureToggle: false },
      fill: true,
      html5: {
        vhs: {
          bandwidth: initialVideoBandwidth(),
          useBandwidthFromLocalStorage: true,
          useDevicePixelRatio: true,
          smoothQualityChange: false,
        },
      },
    }
  }, [])

  useEffect(() => {
    if (ready) player.current?.poster(thumbnail || '')
  }, [ready, thumbnail])

  const source = useMemo(() => {
    return [
      ...(videoManifest
        ? [
            {
              src: videoManifest,
              type: /\.mpd(?:\?|$)/.test(videoManifest) ? 'application/dash+xml' : 'application/x-mpegURL',
            },
          ]
        : []),
      ...(src ? [{ src }] : []),
    ]
  }, [videoManifest, src])

  const setPlayerSrc = useCallback(() => {
    if (visible && ready && source) {
      setVideoRatio(null)
      player.current?.src(source)
    }
  }, [visible, ready, source])

  useEffect(() => {
    setPlayerSrc()

    return () => {
      player.current?.reset()
    }
  }, [setPlayerSrc])

  useEffect(() => {
    const videoPlayer = videojs(videoNode.current as HTMLVideoElement, videoJsOptions, () => setReady(true))
    player.current = videoPlayer
    const updateRatio = () => {
      const width = videoPlayer.videoWidth()
      const height = videoPlayer.videoHeight()
      if (width && height) setVideoRatio(width / height)
    }
    videoPlayer.on(['loadedmetadata', 'resize'], updateRatio)

    return () => {
      videoPlayer.off(['loadedmetadata', 'resize'], updateRatio)
      videoPlayer.dispose()
    }
  }, [videoJsOptions])

  return (
    <div ref={boxRef} className={[styles.boxVideo, 'video-gallery'].join(' ')}>
      <div className={styles.playerFrame} style={size}>
        <video className="video-js" ref={videoNode} disablePictureInPicture />
        <VideoQuality player={ready ? player.current : null} />
      </div>
    </div>
  )
}

export default VideoGallery
